import {
  AdjustmentsIcon,
  PlusIcon,
  SearchIcon,
} from "@heroicons/react/outline";
import Link from "next/link";
import React, { useState } from "react";
import { getValidDate } from "ui/lib/helpers";
import { cn } from "ui/lib/utils";
import { BottomSheet } from "./bottom-sheet";
import { Chip } from "./chip";
import { useLargeTitle } from "./large-title";
import { SheetButton } from "./sheet-button";

/**
 * The one header every admin tool uses below lg: a 28px title with a one-line
 * description, a labelled create pill in the title row, then search and a
 * Filters sheet. The desktop header stays as it was, so a page renders this
 * beside its desktop header and hides that one below lg (`max-lg:hidden`).
 */
export const AdminPageHeader = ({
  title,
  description,
  action,
  children,
  className,
}: {
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
  /** Search, filters and segments, under the title. */
  children?: React.ReactNode;
  className?: string;
}) => {
  const titleRef = React.useRef<HTMLHeadingElement>(null);
  useLargeTitle(titleRef);
  return (
    <div className={cn("space-y-3 pb-3 lg:hidden", className)}>
      <div>
        <div className="flex min-h-11 items-center justify-between gap-3">
          <h1
            ref={titleRef}
            className="min-w-0 text-[1.75rem] font-semibold leading-tight tracking-tight text-white/90"
          >
            {title}
          </h1>
          {action}
        </div>
        {description ? (
          <p className="mt-1 text-[0.9375rem] leading-snug text-white/55">
            {description}
          </p>
        ) : null}
      </div>
      {children}
    </div>
  );
};

const createClass =
  "inline-flex h-11 shrink-0 items-center gap-1.5 rounded-full bg-primary pl-3.5 pr-4 text-[0.9375rem] font-semibold text-primary-foreground transition-opacity active:opacity-80 disabled:opacity-50";

/** The create action in the title row: a labelled 44px volt pill. */
export const AdminCreateButton = ({
  label,
  href,
  onClick,
  disabled,
}: {
  label: string;
  href?: string;
  onClick?: () => void;
  disabled?: boolean;
}) => {
  const body = (
    <>
      <PlusIcon aria-hidden="true" className="size-5" />
      {label}
    </>
  );
  return href ? (
    <Link href={href} className={createClass}>
      {body}
    </Link>
  ) : (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={createClass}
    >
      {body}
    </button>
  );
};

/** The search field and, when the tool has them, its Filters button. */
export const AdminSearchRow = ({
  value,
  onChange,
  placeholder,
  filterCount,
  onFilters,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  filterCount?: number;
  onFilters?: () => void;
}) => (
  <div className="flex items-center gap-2">
    <label className="relative min-w-0 flex-1">
      <span className="sr-only">{placeholder}</span>
      <SearchIcon
        aria-hidden="true"
        className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-faint"
      />
      <input
        type="search"
        inputMode="search"
        enterKeyHint="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="h-11 w-full rounded-xl border border-white/[0.06] bg-white/[0.05] pl-10 pr-3 text-base text-white/90 placeholder:text-white/40 focus:border-white/20 focus:outline-none"
      />
    </label>
    {onFilters ? (
      <AdminToolbarButton
        icon={AdjustmentsIcon}
        onClick={onFilters}
        active={!!filterCount}
        aria-haspopup="dialog"
      >
        Filters
        {filterCount ? (
          <span className="tabular-nums text-white/60">({filterCount})</span>
        ) : null}
      </AdminToolbarButton>
    ) : null}
  </div>
);

/** A 44px secondary control beside the search: Filters, Reorder. */
export const AdminToolbarButton = ({
  icon: Icon,
  active,
  children,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  /** Draws it set, e.g. while filters apply. */
  active?: boolean;
}) => (
  <button
    type="button"
    {...props}
    className={cn(
      "inline-flex h-11 shrink-0 items-center gap-1.5 rounded-xl border px-3.5 text-[0.9375rem] transition-colors active:bg-white/[0.06]",
      active
        ? "border-white/30 bg-white/[0.08] text-white"
        : "border-white/[0.08] text-white/80",
      className
    )}
  >
    <Icon aria-hidden className="size-5" />
    {children}
  </button>
);

/** A list section's caps label, 13px like the inset lists elsewhere. */
export const AdminSectionLabel = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <h2
    className={cn(
      "px-1 pb-1 pt-4 text-[0.8125rem] font-medium uppercase tracking-wide text-faint",
      className
    )}
  >
    {children}
  </h2>
);

export type FilterGroup = {
  title: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
};

/** Every filter a tool's desktop bar has, as one sheet of single-choice chips. */
export const AdminFilterSheet = ({
  open,
  onOpenChange,
  groups,
  onClear,
  resultLabel,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groups: FilterGroup[];
  onClear: () => void;
  resultLabel: string;
}) => (
  <BottomSheet
    open={open}
    onOpenChange={onOpenChange}
    title="Filters"
    full
    footer={
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onClear}
          disabled={!groups.some((group) => group.value)}
          className="min-h-11 rounded-xl px-4 text-[0.9375rem] text-white/80 transition-colors active:bg-white/[0.06] disabled:text-white/30"
        >
          Clear all
        </button>
        <SheetButton
          kind="primary"
          onClick={() => onOpenChange(false)}
          className="flex-1"
        >
          {resultLabel}
        </SheetButton>
      </div>
    }
  >
    <div className="divide-y divide-white/[0.06]">
      {groups.map((group) => {
        const id = `admin-filter-${group.title.toLowerCase().replace(/\W+/g, "-")}`;
        return (
          <section key={group.title} aria-labelledby={id} className="px-2 py-3">
            <h3
              id={id}
              className="mb-2.5 text-[0.8125rem] font-medium uppercase tracking-wide text-white/55"
            >
              {group.title}
            </h3>
            <div
              role="radiogroup"
              aria-labelledby={id}
              className="flex flex-wrap gap-2"
            >
              {group.options.map((option) => (
                <Chip
                  key={option.value || "all"}
                  radio
                  selected={group.value === option.value}
                  onClick={() => group.onChange(option.value)}
                >
                  <span className="truncate">{option.label}</span>
                </Chip>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  </BottomSheet>
);

/** Filters-sheet state for a page: open flag plus how many filters are set. */
export const useFilterSheet = (groups: FilterGroup[]) => {
  const [open, setOpen] = useState(false);
  return {
    open,
    setOpen,
    count: groups.filter((group) => group.value).length,
  };
};

/**
 * One date format for admin lists: "Oct 20" this year, "Oct 20, 2025"
 * otherwise. Rocky's offset-less times are UTC.
 */
export const formatAdminDate = (value?: string | Date | null) => {
  const date =
    value instanceof Date ? value : value ? getValidDate(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
};
